import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  ClockIcon,
  UserGroupIcon,
  DocumentChartBarIcon,
  CogIcon,
  ShieldCheckIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  MapPinIcon,
  ShieldExclamationIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
  RocketLaunchIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import { initPush } from '../utils/pushNotifications';
import DashboardSwitcher from './DashboardSwitcher';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Attendance', href: '/attendance', icon: ClockIcon },
  { name: 'Users', href: '/users', icon: UserGroupIcon },
  { name: 'Team Hierarchy', href: '/team-hierarchy', icon: UserGroupIcon },
  { name: 'Leave Management', href: '/leave-management', icon: CalendarDaysIcon },
  { name: 'Locations', href: '/locations', icon: MapPinIcon },
  { name: 'Feature Announcements', href: '/feature-announcements', icon: RocketLaunchIcon },
  { name: 'ClickUp Analytics', href: '/clickup-analytics', icon: ChartBarIcon },
  { name: 'Orbit Analytics', href: '/orbit-analytics', icon: ChartBarIcon },
  { name: 'Announcements', href: '/admin-announcements', icon: BellIcon },
  { name: 'Feedback Inbox', href: '/feedback-inbox', icon: ChatBubbleLeftRightIcon },
  { name: 'Device Security', href: '/device-security', icon: ShieldCheckIcon },
  { name: 'KPI', href: '/kpi', icon: ChartBarIcon },
  { name: 'KPI (Manager)', href: '/kpi/manager', icon: ChartBarIcon },
  { name: 'Reports', href: '/reports', icon: DocumentChartBarIcon },
  { name: 'Login Logs', href: '/login-logs', icon: ShieldCheckIcon },
  { name: 'Security Logs', href: '/security-logs', icon: ShieldExclamationIcon },
  { name: 'Email Logs', href: '/email-logs', icon: EnvelopeIcon },
  { name: 'Email Stats', href: '/email-stats', icon: EnvelopeIcon },
  { name: 'Settings', href: '/settings', icon: CogIcon },
];

function getUserInitials(user) {
  const names = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .map((name) => String(name).trim()[0])
    .filter(Boolean);

  if (names.length > 0) {
    return names.join('').slice(0, 2).toUpperCase();
  }

  return String(user?.email || 'A').trim()[0]?.toUpperCase() || 'A';
}

function CurrentUserAvatar({ user, className = 'h-9 w-9' }) {
  const baseClassName = `${className} flex-shrink-0 overflow-hidden rounded-full border border-slate-200 bg-slate-100`;

  if (user?.profilePhotoUrl) {
    return (
      <img
        src={user.profilePhotoUrl}
        alt={user?.email ? `${user.email} profile` : 'Profile'}
        className={`${baseClassName} object-cover`}
      />
    );
  }

  return (
    <div className={`${baseClassName} flex items-center justify-center text-xs font-semibold text-slate-600`}>
      {getUserInitials(user)}
    </div>
  );
}

function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const { user, logout, isSuperAdmin, isManagerAdmin } = useAuth();

  useEffect(() => {
    if (user) {
      // Get token from localStorage to pass to initPush
      const token = localStorage.getItem('token');
      initPush(token);
    }
  }, [user]);

  // Filter navigation items based on user permissions
  const filteredNavigation = navigation.filter(item => {
    // Settings only for super admin
    if (item.name === 'Settings') {
      return isSuperAdmin();
    }
    // Email Logs only for super admin
    if (item.name === 'Email Logs') {
      return isSuperAdmin();
    }
    // Email Stats only for super admin
    if (item.name === 'Email Stats') {
      return isSuperAdmin();
    }
    // Feature Announcements only for super admin
    if (item.name === 'Feature Announcements') {
      return isSuperAdmin();
    }
    // KPI visible to all admins
    if (item.name === 'KPI') return true;
    // KPI (Manager) visible only to manager admins (not super admins - they use KPI tab)
    if (item.name === 'KPI (Manager)') return isManagerAdmin() && !isSuperAdmin();
    // All other items visible to all admins/manager-admins
    return true;
  });

  const handleLogout = () => {
    logout();
    // No need to navigate here - logout() handles it with window.location
  };


  const isSuperAdminUser = isSuperAdmin();

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 md:hidden ${sidebarOpen ? 'flex' : 'hidden'}`} aria-modal="true" role="dialog">
        <div
          className="fixed inset-0 bg-slate-900/60"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white shadow-xl">
          <div className="absolute top-0 right-0 -mr-10 pt-3">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              aria-label="Close sidebar"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                  Admin Control Center
                </h1>
                {isSuperAdminUser && (
                  <p className="mt-1 inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-600">
                    Super Admin
                  </p>
                )}
              </div>
            </div>
            <nav className="mt-5 px-2 space-y-1">
              {filteredNavigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      isActive
                        ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-500'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-4 border-transparent'
                    } group flex items-center px-3 py-2.5 text-base font-medium rounded-xl`}
                  >
                    <item.icon
                      className={`${
                        isActive ? 'text-indigo-500' : 'text-slate-400 group-hover:text-slate-500'
                      } mr-4 h-6 w-6`}
                    />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="border-t border-slate-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <CurrentUserAvatar user={user} />
                <div className="min-w-0 text-xs text-slate-600">
                  <div className="font-medium truncate max-w-[140px]">
                    {user?.email || 'Admin User'}
                  </div>
                  {isSuperAdminUser && (
                    <div className="mt-0.5 inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-600">
                      Super Admin
                    </div>
                  )}
                  {user?.department && !isSuperAdminUser && (
                    <div className="text-xs text-blue-600 font-medium mt-0.5">
                      {user.department}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <NotificationBell />
                <button
                  onClick={() => {
                    setSidebarOpen(false);
                    handleLogout();
                  }}
                  className="text-slate-400 hover:text-slate-600 focus:outline-none"
                  title="Logout"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="text-xs text-slate-400 text-center">
              © 2025 CoachLMS EMS
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div
        className={`hidden md:flex md:flex-col md:fixed md:inset-y-0 transition-all duration-300 ${
          sidebarCollapsed ? 'md:w-20' : 'md:w-72'
        }`}
      >
        <div className="flex-1 flex flex-col min-h-0 border-r border-slate-200 bg-white/90 backdrop-blur">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center justify-between flex-shrink-0 px-4">
              {!sidebarCollapsed && (
                <div>
                  <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                    Admin Control Center
                  </h1>
                  {isSuperAdminUser && (
                    <p className="mt-1 inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-600">
                      Super Admin
                    </p>
                  )}
                </div>
              )}
              <button
                type="button"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <Bars3Icon className={`h-4 w-4 transform transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`} />
              </button>
            </div>
            <nav className="mt-5 flex-1 px-3 space-y-1">
              {filteredNavigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      isActive
                        ? 'bg-indigo-50 text-indigo-700 border-l-4 border-indigo-500 shadow-sm'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-4 border-transparent'
                    } group flex items-center px-3 py-2 text-sm font-medium rounded-xl`}
                  >
                    <item.icon
                      className={`${
                        isActive ? 'text-indigo-500' : 'text-slate-400 group-hover:text-slate-500'
                      } h-5 w-5 ${sidebarCollapsed ? 'mx-auto' : 'mr-3'}`}
                    />
                    {!sidebarCollapsed && (
                      <span className="truncate">{item.name}</span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex-shrink-0 flex flex-col border-t border-slate-200 bg-white/90 backdrop-blur p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex min-w-0 items-center gap-3 text-xs text-slate-600">
                <CurrentUserAvatar user={user} className={sidebarCollapsed ? 'h-8 w-8' : 'h-9 w-9'} />
                {!sidebarCollapsed && (
                  <div className="min-w-0">
                    <div className="font-medium truncate max-w-[150px]">
                      {user?.email || 'Admin User'}
                    </div>
                    {isSuperAdminUser && (
                      <div className="mt-0.5 inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-600">
                        Super Admin
                      </div>
                    )}
                    {user?.department && !isSuperAdminUser && (
                      <div className="text-xs text-blue-600 font-medium mt-0.5">
                        {user.department}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <NotificationBell />
                <button
                  onClick={handleLogout}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  title="Logout"
                  aria-label="Logout"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            {!sidebarCollapsed && (
              <div className="text-xs text-slate-500">© 2025 CoachLMS EMS</div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`flex flex-col flex-1 min-h-0 bg-slate-50 transition-all duration-300 ${
        sidebarCollapsed ? 'md:pl-20' : 'md:pl-72'
      }`}>
        {/* Mobile top bar */}
        <div className="sticky top-0 z-30 md:hidden bg-white/90 backdrop-blur border-b border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="inline-flex items-center justify-center h-11 w-11 rounded-xl border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open sidebar"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              <CurrentUserAvatar user={user} />
              <div>
                <h1 className="text-base font-semibold text-slate-900 leading-tight">
                  Admin Control Center
                </h1>
                <p className="text-xs text-slate-500 truncate max-w-[160px]">
                  {user?.email || 'Admin User'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <DashboardSwitcher />
              <NotificationBell />
              <button
                type="button"
                onClick={() => {
                  setSidebarOpen(false);
                  handleLogout();
                }}
                className="inline-flex items-center justify-center h-11 w-11 rounded-xl border border-slate-200 text-slate-500 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                title="Logout"
                aria-label="Logout"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Desktop top-right dashboard switcher */}
        <div className="hidden md:flex justify-end px-4 sm:px-6 lg:px-8 pt-4">
          <DashboardSwitcher />
        </div>

        <main className="flex-1 relative overflow-y-auto focus:outline-none min-h-0">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Layout;
